import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Switch,
  Spin,
  Popconfirm,
  Select,
  InputNumber,
  Collapse,
  Typography,
  Tooltip,
  Card,
  Image,
  Avatar,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HolderOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  PlayCircleOutlined,
  UserOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PagesWrapper from '../../PagesWrapper';
import ImageDropZone from '../../components/ImageDropZone';
import { AccessBanner, OwnershipManager } from '../../components/Ownership';
import { useOwnership } from '../../hooks/useOwnership';
import surveysService, { ChoiceType, ChoiceTypeLabels } from '../../services/surveysService';
import { getFileUrl } from '../../utils/urlUtils';
import styles from './SurveyEditor.module.css';

/**
 * Helper to handle auto-unpublish validation results from API responses.
 * Shows a warning message and updates survey state if survey was auto-unpublished.
 */
const handleAutoUnpublishResult = (validationResult, setSurvey, t) => {
  if (validationResult?.wasAutoUnpublished) {
    const errorList = validationResult.errors?.join(', ') || 'Survey is now invalid';
    Modal.warning({
      title: t('surveys.autoUnpublished.title', 'Survey Unpublished'),
      content: t(
        'surveys.autoUnpublished.message',
        'Your survey was automatically unpublished because it is no longer valid: {{errors}}. Please fix the issues and publish again.',
        { errors: errorList }
      ),
    });
    setSurvey(prev => ({ ...prev, isPublished: false }));
  }
};

const { TextArea } = Input;
const { Text } = Typography;
const { Panel } = Collapse;

// Sortable Question Item
const SortableQuestion = ({ question, index, onEdit, onDelete, onAddChoice, onEditChoice, onDeleteChoice, onReorderChoices }) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChoiceDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = question.choices.findIndex(c => c.id === active.id);
      const newIndex = question.choices.findIndex(c => c.id === over.id);
      const newOrder = arrayMove(question.choices, oldIndex, newIndex);
      onReorderChoices(question.id, newOrder.map(c => c.id));
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.questionCard} ${isDragging ? styles.dragging : ''}`}
    >
      <div className={styles.questionHeader} {...attributes} {...listeners}>
        <HolderOutlined className={styles.dragHandle} />
        <span className={styles.questionNumber}>Q{index + 1}</span>
        <span className={styles.questionName}>{question.name}</span>
        {question.allowMultipleChoices && (
          <Tag color="geekblue" className={styles.multipleTag}>
            {t('surveys.multiSelect', 'Multi-select')}
          </Tag>
        )}
        {question.isRequired && (
          <Tag color="red" className={styles.requiredTag}>
            {t('surveys.required', 'Required')}
          </Tag>
        )}
        <Space className={styles.questionActions} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <Tooltip title={t('surveys.editQuestion', 'Edit Question')}>
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(question)} />
          </Tooltip>
          <Popconfirm
            title={t('surveys.deleteQuestionConfirm', 'Delete this question?')}
            onConfirm={() => onDelete(question.id)}
            okText={t('common.yes', 'Yes')}
            cancelText={t('common.no', 'No')}
          >
            <Tooltip title={t('surveys.deleteQuestion', 'Delete Question')}>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      </div>

      <div className={styles.questionContent}>
        <p className={styles.questionBodyText}>{question.body}</p>
        
        {question.imageUrl && (
          <Image 
            src={question.imageUrl} 
            alt="" 
            className={styles.imagePreview}
            style={{ maxWidth: 200, maxHeight: 150, objectFit: 'contain' }}
          />
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleChoiceDragEnd}
        >
          <SortableContext
            items={question.choices.map(c => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className={styles.choicesList}>
              {question.choices.map((choice, choiceIndex) => (
                <SortableChoice
                  key={choice.id}
                  choice={choice}
                  index={choiceIndex}
                  onEdit={() => onEditChoice(question, choice)}
                  onDelete={() => onDeleteChoice(choice.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => onAddChoice(question)}
          className={styles.addChoiceButton}
        >
          {t('surveys.addChoice', 'Add Choice')}
        </Button>
      </div>
    </div>
  );
};

// Sortable Choice Item
const SortableChoice = ({ choice, index, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: choice.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getChoiceDisplay = () => {
    switch (choice.choiceType) {
      case ChoiceType.Range:
        return (
          <span className={styles.rangeInfo}>
            {choice.text && <strong>{choice.text}: </strong>}
            {t('surveys.range', 'Range')}: {choice.rangeMin} - {choice.rangeMax}
            {choice.rangeStep && ` (step: ${choice.rangeStep})`}
          </span>
        );
      case ChoiceType.FreeText:
        return (
          <span className={styles.freeTextInfo}>
            {t('surveys.freeText', 'Free Text')}
            {choice.placeholder && `: "${choice.placeholder}"`}
            {choice.maxCharacters && ` (max: ${choice.maxCharacters} chars)`}
          </span>
        );
      default:
        return <span className={styles.choiceText}>{choice.text || t('surveys.noText', '(no text)')}</span>;
    }
  };

  const getChoiceTypeColor = () => {
    switch (choice.choiceType) {
      case ChoiceType.Range: return 'purple';
      case ChoiceType.FreeText: return 'cyan';
      default: return 'blue';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.choiceItem} ${isDragging ? styles.dragging : ''}`}
    >
      <HolderOutlined className={styles.choiceDragHandle} {...attributes} {...listeners} />
      <Tag color={getChoiceTypeColor()} className={styles.choiceTypeTag}>
        {ChoiceTypeLabels[choice.choiceType]}
      </Tag>
      {getChoiceDisplay()}
      {choice.imageUrl && (
        <Image
          src={choice.imageUrl}
          alt=""
          width={64}
          height={64}
          style={{objectFit: 'cover', borderRadius: 4}}
          preview={{ mask: null }}
        />
      )}
      <Space className={styles.choiceActions}>
        <Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit} />
        <Popconfirm
          title={t('surveys.deleteChoiceConfirm', 'Delete this choice?')}
          onConfirm={onDelete}
          okText={t('common.yes', 'Yes')}
          cancelText={t('common.no', 'No')}
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    </div>
  );
};

const SurveyEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [detailsForm] = Form.useForm();
  const [questionForm] = Form.useForm();
  const [choiceForm] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);

  // Modal states
  const [questionModalVisible, setQuestionModalVisible] = useState(false);
  const [choiceModalVisible, setChoiceModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingChoice, setEditingChoice] = useState(null);
  const [parentQuestion, setParentQuestion] = useState(null);

  // Image states for forms
  const [surveyImage, setSurveyImage] = useState(null);
  const [clearSurveyImage, setClearSurveyImage] = useState(false);
  const [questionImage, setQuestionImage] = useState(null);
  const [clearQuestionImage, setClearQuestionImage] = useState(false);
  const [choiceImage, setChoiceImage] = useState(null);
  const [clearChoiceImage, setClearChoiceImage] = useState(false);

  // Ownership management
  const [ownershipModalVisible, setOwnershipModalVisible] = useState(false);
  const {
    ownershipInfo,
    loading: ownershipLoading,
    canManageOwnership,
    shareWith,
    revoke,
    getAvailableUsers,
    availableUsers,
    loadingAvailableUsers,
  } = useOwnership('Survey', id ? parseInt(id, 10) : null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchSurvey = useCallback(async () => {
    try {
      setLoading(true);
      const data = await surveysService.getSurvey(id);
      setSurvey(data);
      setQuestions(data.questions || []);
      detailsForm.setFieldsValue({
        name: data.name,
        introText: data.introText,
        finishText: data.finishText,
        allowAnonymous: data.allowAnonymous,
        enableIpRateLimit: data.enableIpRateLimit,
        maxParticipationsPerIp: data.maxParticipationsPerIp,
        enableParticipationTimeout: data.enableParticipationTimeout,
        participationTimeoutMinutes: data.participationTimeoutMinutes,
      });
    } catch (error) {
      message.error(t('surveys.loadError', 'Failed to load survey'));
      navigate('/surveys');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t, detailsForm]);

  useEffect(() => {
    fetchSurvey();
  }, [fetchSurvey]);

  const handleSaveDetails = async () => {
    try {
      const values = await detailsForm.validateFields();
      setSaving(true);
      
      // Build update data
      const updateData = { ...values };
      if (clearSurveyImage) {
        updateData.clearImage = true;
      }
      
      await surveysService.updateSurvey(id, updateData, surveyImage);
      message.success(t('surveys.saveSuccess', 'Survey saved successfully'));
      
      // Reset image state after save
      setSurveyImage(null);
      setClearSurveyImage(false);
      
      await fetchSurvey();
    } catch (error) {
      if (!error.errorFields) {
        message.error(t('surveys.saveError', 'Failed to save survey'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    try {
      setPublishing(true);
      if (survey.isPublished) {
        await surveysService.unpublishSurvey(id);
        setSurvey(prev => ({ ...prev, isPublished: false }));
        message.success(t('surveys.unpublishSuccess', 'Survey unpublished'));
      } else {
        await surveysService.publishSurvey(id);
        setSurvey(prev => ({ ...prev, isPublished: true }));
        message.success(t('surveys.publishSuccess', 'Survey published'));
      }
    } catch (error) {
      const errors = error.response?.data?.errors;
      if (errors && errors.length > 0) {
        Modal.error({
          title: t('surveys.validation.cannotPublish', 'Cannot Publish Survey'),
          content: (
            <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
              {errors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          ),
        });
      } else {
        message.error(error.response?.data?.message || t('surveys.togglePublishError', 'Failed to update survey status'));
      }
    } finally {
      setPublishing(false);
    }
  };

  // Question handlers
  const handleAddQuestion = () => {
    setEditingQuestion(null);
    questionForm.resetFields();
    questionForm.setFieldsValue({ isRequired: true, allowMultipleChoices: false });
    setQuestionImage(null);
    setClearQuestionImage(false);
    setQuestionModalVisible(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    questionForm.setFieldsValue({
      name: question.name,
      body: question.body,
      isRequired: question.isRequired,
      allowMultipleChoices: question.allowMultipleChoices,
    });
    setQuestionImage(null);
    setClearQuestionImage(false);
    setQuestionModalVisible(true);
  };

  const handleQuestionModalOk = async () => {
    try {
      const values = await questionForm.validateFields();
      setSaving(true);

      // Build update/create data
      const questionData = { ...values };
      if (clearQuestionImage) {
        questionData.clearImage = true;
      }

      if (editingQuestion) {
        const updatedQuestion = await surveysService.updateQuestion(editingQuestion.id, questionData, questionImage);
        // Update local state with the response, preserving existing choices
        setQuestions(prev => prev.map(q => 
          q.id === editingQuestion.id ? { ...q, ...updatedQuestion, choices: q.choices } : q
        ));
        message.success(t('surveys.questionUpdated', 'Question updated'));
      } else {
        const response = await surveysService.addQuestion(id, questionData, questionImage);
        // Response now includes { question, validationResult }
        const newQuestion = response.question || response;
        // Add new question to local state
        setQuestions(prev => [...prev, { ...newQuestion, choices: newQuestion.choices || [] }]);
        message.success(t('surveys.questionAdded', 'Question added'));
        // Check for auto-unpublish
        handleAutoUnpublishResult(response.validationResult, setSurvey, t);
      }

      setQuestionModalVisible(false);
      setQuestionImage(null);
      setClearQuestionImage(false);
    } catch (error) {
      if (!error.errorFields) {
        message.error(t('surveys.questionSaveError', 'Failed to save question'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      const response = await surveysService.deleteQuestion(questionId);
      // Update local state
      setQuestions(prev => prev.filter(q => q.id !== questionId));
      message.success(t('surveys.questionDeleted', 'Question deleted'));
      // Check for auto-unpublish (response may include { success, validationResult })
      handleAutoUnpublishResult(response?.validationResult, setSurvey, t);
    } catch (error) {
      message.error(t('surveys.questionDeleteError', 'Failed to delete question'));
    }
  };

  const handleQuestionDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = questions.findIndex(q => q.id === active.id);
      const newIndex = questions.findIndex(q => q.id === over.id);
      const newOrder = arrayMove(questions, oldIndex, newIndex);
      setQuestions(newOrder);

      try {
        await surveysService.reorderQuestions(id, newOrder.map(q => q.id));
      } catch (error) {
        message.error(t('surveys.reorderError', 'Failed to reorder questions'));
        await fetchSurvey();
      }
    }
  };

  // Choice handlers
  const handleAddChoice = (question) => {
    setParentQuestion(question);
    setEditingChoice(null);
    choiceForm.resetFields();
    choiceForm.setFieldsValue({ choiceType: ChoiceType.Normal });
    setChoiceImage(null);
    setClearChoiceImage(false);
    setChoiceModalVisible(true);
  };

  const handleEditChoice = (question, choice) => {
    setParentQuestion(question);
    setEditingChoice(choice);
    choiceForm.setFieldsValue({
      choiceType: choice.choiceType,
      text: choice.text,
      rangeMin: choice.rangeMin,
      rangeMax: choice.rangeMax,
      rangeStep: choice.rangeStep,
      rangeMinLabel: choice.rangeMinLabel,
      rangeMaxLabel: choice.rangeMaxLabel,
      maxCharacters: choice.maxCharacters,
      placeholder: choice.placeholder,
    });
    setChoiceImage(null);
    setClearChoiceImage(false);
    setChoiceModalVisible(true);
  };

  const handleChoiceModalOk = async () => {
    try {
      const values = await choiceForm.validateFields();
      setSaving(true);

      // Build update/create data
      const choiceData = { ...values };
      if (clearChoiceImage) {
        choiceData.clearImage = true;
      }

      // Only include image for Normal choice type
      const imageToSend = values.choiceType === ChoiceType.Normal ? choiceImage : null;

      if (editingChoice) {
        const response = await surveysService.updateChoice(editingChoice.id, choiceData, imageToSend);
        // Response now includes { choice, validationResult }
        const updatedChoice = response.choice || response;
        // Update local state with the response
        setQuestions(prev => prev.map(q => 
          q.id === parentQuestion.id 
            ? { ...q, choices: q.choices.map(c => c.id === editingChoice.id ? updatedChoice : c) }
            : q
        ));
        message.success(t('surveys.choiceUpdated', 'Choice updated'));
        // Check for auto-unpublish
        handleAutoUnpublishResult(response.validationResult, setSurvey, t);
      } else {
        const response = await surveysService.addChoice(parentQuestion.id, choiceData, imageToSend);
        // Response now includes { choice, validationResult }
        const newChoice = response.choice || response;
        // Add new choice to local state
        setQuestions(prev => prev.map(q => 
          q.id === parentQuestion.id 
            ? { ...q, choices: [...q.choices, newChoice] }
            : q
        ));
        message.success(t('surveys.choiceAdded', 'Choice added'));
        // Check for auto-unpublish
        handleAutoUnpublishResult(response.validationResult, setSurvey, t);
      }

      setChoiceModalVisible(false);
      setChoiceImage(null);
      setClearChoiceImage(false);
    } catch (error) {
      if (!error.errorFields) {
        message.error(t('surveys.choiceSaveError', 'Failed to save choice'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChoice = async (choiceId) => {
    try {
      const response = await surveysService.deleteChoice(choiceId);
      // Update local state
      setQuestions(prev => prev.map(q => ({
        ...q,
        choices: q.choices.filter(c => c.id !== choiceId)
      })));
      message.success(t('surveys.choiceDeleted', 'Choice deleted'));
      // Check for auto-unpublish (response may include { success, validationResult })
      handleAutoUnpublishResult(response?.validationResult, setSurvey, t);
    } catch (error) {
      message.error(t('surveys.choiceDeleteError', 'Failed to delete choice'));
    }
  };

  const handleReorderChoices = async (questionId, choiceIds) => {
    // Update local state optimistically
    setQuestions(prev => prev.map(q => {
      if (q.id !== questionId) return q;
      const reorderedChoices = choiceIds.map(cId => q.choices.find(c => c.id === cId)).filter(Boolean);
      return { ...q, choices: reorderedChoices };
    }));

    try {
      await surveysService.reorderChoices(questionId, choiceIds);
    } catch (error) {
      message.error(t('surveys.reorderError', 'Failed to reorder choices'));
      // Revert on error by refetching
      await fetchSurvey();
    }
  };

  const choiceType = Form.useWatch('choiceType', choiceForm);

  if (loading) {
    return (
      <PagesWrapper>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.editorContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/surveys')}>
            {t('common.back', 'Back')}
          </Button>
          <h1>{survey?.name || t('surveys.untitled', 'Untitled Survey')}</h1>
          <Tag color={survey?.isPublished ? 'green' : 'default'}>
            {survey?.isPublished ? t('surveys.published', 'Published') : t('surveys.draft', 'Draft')}
          </Tag>
          {survey?.addedByName && (
            <Tooltip title={t('surveys.createdBy', 'Created by')}>
              <Space size="small" className={styles.createdBy}>
                <Avatar 
                  size="small" 
                  src={getFileUrl(survey.addedByProfileImageUrl)}
                  icon={!survey.addedByProfileImageUrl && <UserOutlined />}
                />
                <Text type="secondary">{survey.addedByName}</Text>
              </Space>
            </Tooltip>
          )}
        </div>
        <Space className={styles.headerActions}>
          {survey?.isPublished && (
            <>
              <Tooltip title={t('surveys.openSurvey', 'Open Survey')}>
                <Button
                  icon={<PlayCircleOutlined />}
                  onClick={() => window.open(`/survey/${id}`, '_blank')}
                >
                  {t('surveys.openSurvey', 'Open Survey')}
                </Button>
              </Tooltip>
              <Tooltip title={t('surveys.statistics', 'Statistics')}>
                <Button
                  icon={<BarChartOutlined />}
                  onClick={() => navigate(`/surveys/${id}/statistics`)}
                >
                  {t('surveys.statistics', 'Statistics')}
                </Button>
              </Tooltip>
            </>
          )}
          {canManageOwnership && (
            <Tooltip title={t('ownership.share', 'Share')}>
              <Button
                icon={<TeamOutlined />}
                onClick={() => setOwnershipModalVisible(true)}
              >
                {t('ownership.share', 'Share')}
              </Button>
            </Tooltip>
          )}
          <Button
            icon={survey?.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            onClick={handleTogglePublish}
            loading={publishing}
          >
            {survey?.isPublished ? t('surveys.unpublish', 'Unpublish') : t('surveys.publish', 'Publish')}
          </Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveDetails} loading={saving}>
            {t('common.save', 'Save')}
          </Button>
        </Space>
      </div>

      {/* Access Banner for shared/admin users */}
      {ownershipInfo && (
        <AccessBanner
          accessLevel={ownershipInfo.accessLevel}
          ownerInfo={ownershipInfo.mainOwner}
          canEdit={ownershipInfo.canEdit}
          canDelete={ownershipInfo.canDelete}
        />
      )}

      {/* Survey Details */}
      <div className={styles.detailsSection}>
        <h3 className={styles.sectionTitle}>{t('surveys.details', 'Survey Details')}</h3>
        <Form form={detailsForm} layout="vertical">
          <Form.Item
            name="name"
            label={t('surveys.surveyName', 'Survey Name')}
            rules={[{ required: true, message: t('surveys.nameRequired', 'Please enter a survey name') }]}
          >
            <Input placeholder={t('surveys.namePlaceholder', 'Enter survey name...')} />
          </Form.Item>

          <Form.Item
            name="introText"
            label={t('surveys.introText', 'Introduction Text')}
          >
            <TextArea
              rows={3}
              placeholder={t('surveys.introTextPlaceholder', 'Text shown at the beginning of the survey...')}
              maxLength={5000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="finishText"
            label={t('surveys.finishText', 'Completion Text')}
          >
            <TextArea
              rows={3}
              placeholder={t('surveys.finishTextPlaceholder', 'Text shown after completing the survey...')}
              maxLength={5000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="allowAnonymous"
            label={t('surveys.allowAnonymous', 'Allow Anonymous Participation')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="enableIpRateLimit"
            label={t('surveys.enableIpRateLimit', 'Enable IP Rate Limiting')}
            valuePropName="checked"
            tooltip={t('surveys.enableIpRateLimitTooltip', 'Limit the number of completed participations from the same IP address')}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.enableIpRateLimit !== currentValues.enableIpRateLimit}
          >
            {({ getFieldValue }) =>
              getFieldValue('enableIpRateLimit') && (
                <Form.Item
                  name="maxParticipationsPerIp"
                  label={t('surveys.maxParticipationsPerIp', 'Max Participations per IP')}
                  tooltip={t('surveys.maxParticipationsPerIpTooltip', 'Maximum number of completed participations allowed from the same IP address')}
                >
                  <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            name="enableParticipationTimeout"
            label={t('surveys.enableParticipationTimeout', 'Enable Participation Timeout')}
            valuePropName="checked"
            tooltip={t('surveys.enableParticipationTimeoutTooltip', 'Allow incomplete participations to expire after a set time, enabling fresh starts')}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.enableParticipationTimeout !== currentValues.enableParticipationTimeout}
          >
            {({ getFieldValue }) =>
              getFieldValue('enableParticipationTimeout') && (
                <Form.Item
                  name="participationTimeoutMinutes"
                  label={t('surveys.participationTimeout', 'Participation Timeout (minutes)')}
                  tooltip={t('surveys.participationTimeoutTooltip', 'After this time, an incomplete participation is considered expired and the user can start fresh')}
                >
                  <InputNumber min={1} max={10080} style={{ width: '100%' }} />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            label={t('surveys.image', 'Survey Image (optional)')}
          >
            <ImageDropZone
              value={surveyImage}
              onChange={(file) => {
                setSurveyImage(file);
                if (file) setClearSurveyImage(false);
              }}
              existingUrl={!clearSurveyImage ? survey?.imageUrl : null}
              onClearExisting={() => {
                setClearSurveyImage(true);
                setSurveyImage(null);
              }}
            />
          </Form.Item>
        </Form>
      </div>

      {/* Questions */}
      <div className={styles.questionsSection}>
        <h3 className={styles.sectionTitle}>
          {t('surveys.questions', 'Questions')} ({questions.length})
        </h3>

        {questions.length === 0 ? (
          <div className={styles.emptyQuestions}>
            <p>{t('surveys.noQuestions', 'No questions yet. Add your first question to get started.')}</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleQuestionDragEnd}
          >
            <SortableContext
              items={questions.map(q => q.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className={styles.questionsList}>
                {questions.map((question, index) => (
                  <SortableQuestion
                    key={question.id}
                    question={question}
                    index={index}
                    onEdit={handleEditQuestion}
                    onDelete={handleDeleteQuestion}
                    onAddChoice={handleAddChoice}
                    onEditChoice={handleEditChoice}
                    onDeleteChoice={handleDeleteChoice}
                    onReorderChoices={handleReorderChoices}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={handleAddQuestion}
          className={styles.addQuestionButton}
          block
        >
          {t('surveys.addQuestion', 'Add Question')}
        </Button>
      </div>

      {/* Question Modal */}
      <Modal
        title={editingQuestion ? t('surveys.editQuestion', 'Edit Question') : t('surveys.addQuestion', 'Add Question')}
        open={questionModalVisible}
        onOk={handleQuestionModalOk}
        onCancel={() => setQuestionModalVisible(false)}
        confirmLoading={saving}
      >
        <Form form={questionForm} layout="vertical" className={styles.modalForm}>
          <Form.Item
            name="name"
            label={t('surveys.questionName', 'Question Name')}
            rules={[{ required: true, message: t('surveys.questionNameRequired', 'Please enter a question name') }]}
          >
            <Input
              placeholder={t('surveys.questionNamePlaceholder', 'Short identifier (e.g., "Age Check")')}
              maxLength={255}
            />
          </Form.Item>

          <Form.Item
            name="body"
            label={t('surveys.questionText', 'Question Text')}
            rules={[{ required: true, message: t('surveys.questionRequired', 'Please enter the question') }]}
          >
            <TextArea
              rows={3}
              placeholder={t('surveys.questionPlaceholder', 'Enter your question...')}
              maxLength={2000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="isRequired"
            label={t('surveys.isRequired', 'Required Question')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="allowMultipleChoices"
            label={t('surveys.allowMultipleChoices', 'Allow Multiple Choices')}
            valuePropName="checked"
            tooltip={t('surveys.allowMultipleChoicesHint', 'Allows selecting multiple Normal choices. Range and Free Text choices can always be answered independently.')}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={t('surveys.questionImage', 'Question Image (optional)')}
          >
            <ImageDropZone
              value={questionImage}
              onChange={(file) => {
                setQuestionImage(file);
                if (file) setClearQuestionImage(false);
              }}
              existingUrl={!clearQuestionImage ? editingQuestion?.imageUrl : null}
              onClearExisting={() => {
                setClearQuestionImage(true);
                setQuestionImage(null);
              }}
              compact
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Choice Modal */}
      <Modal
        title={editingChoice ? t('surveys.editChoice', 'Edit Choice') : t('surveys.addChoice', 'Add Choice')}
        open={choiceModalVisible}
        onOk={handleChoiceModalOk}
        onCancel={() => setChoiceModalVisible(false)}
        confirmLoading={saving}
        width={500}
      >
        <Form form={choiceForm} layout="vertical" className={styles.modalForm}>
          <Form.Item
            name="choiceType"
            label={t('surveys.choiceType', 'Choice Type')}
            rules={[{ required: true }]}
          >
            <Select className={styles.choiceTypeSelect}>
              {Object.entries(ChoiceTypeLabels).map(([value, label]) => (
                <Select.Option key={value} value={Number(value)}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {choiceType === ChoiceType.Normal && (
            <Form.Item
              name="text"
              label={t('surveys.choiceText', 'Choice Text')}
              rules={[{ required: true, message: t('surveys.choiceTextRequired', 'Please enter choice text') }]}
            >
              <Input placeholder={t('surveys.choiceTextPlaceholder', 'Enter choice text...')} />
            </Form.Item>
          )}

          {choiceType === ChoiceType.Normal && (
            <Form.Item
              label={t('surveys.choiceImage', 'Choice Image (optional)')}
            >
              <ImageDropZone
                value={choiceImage}
                onChange={(file) => {
                  setChoiceImage(file);
                  if (file) setClearChoiceImage(false);
                }}
                existingUrl={!clearChoiceImage ? editingChoice?.imageUrl : null}
                onClearExisting={() => {
                  setClearChoiceImage(true);
                  setChoiceImage(null);
                }}
                compact
              />
            </Form.Item>
          )}

          {choiceType === ChoiceType.Range && (
            <>
              <Form.Item
                name="text"
                label={t('surveys.rangeLabel', 'Label (optional)')}
                tooltip={t('surveys.rangeLabelHint', 'Optional label shown above the slider')}
              >
                <Input placeholder={t('surveys.rangeLabelPlaceholder', 'e.g., Rate your satisfaction')} />
              </Form.Item>

              <div className={styles.rangeFields}>
                <Form.Item
                  name="rangeMin"
                  label={t('surveys.rangeMin', 'Min Value')}
                  rules={[{ required: true, message: t('surveys.rangeMinRequired', 'Required') }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                  name="rangeMax"
                  label={t('surveys.rangeMax', 'Max Value')}
                  rules={[{ required: true, message: t('surveys.rangeMaxRequired', 'Required') }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                  name="rangeStep"
                  label={t('surveys.rangeStep', 'Step')}
                >
                  <InputNumber style={{ width: '100%' }} min={0.01} />
                </Form.Item>
              </div>

              <Form.Item
                name="rangeMinLabel"
                label={t('surveys.rangeMinLabel', 'Min Label (optional)')}
              >
                <Input placeholder={t('surveys.rangeMinLabelPlaceholder', 'e.g., Strongly Disagree')} />
              </Form.Item>

              <Form.Item
                name="rangeMaxLabel"
                label={t('surveys.rangeMaxLabel', 'Max Label (optional)')}
              >
                <Input placeholder={t('surveys.rangeMaxLabelPlaceholder', 'e.g., Strongly Agree')} />
              </Form.Item>
            </>
          )}

          {choiceType === ChoiceType.FreeText && (
            <>
              <Form.Item
                name="maxCharacters"
                label={t('surveys.maxCharacters', 'Max Characters (optional)')}
              >
                <InputNumber style={{ width: '100%' }} min={1} max={10000} />
              </Form.Item>

              <Form.Item
                name="placeholder"
                label={t('surveys.placeholder', 'Placeholder Text (optional)')}
              >
                <Input placeholder={t('surveys.placeholderPlaceholder', 'Enter placeholder text...')} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* Ownership Manager Modal */}
      <OwnershipManager
        open={ownershipModalVisible}
        onClose={() => setOwnershipModalVisible(false)}
        ownershipInfo={ownershipInfo}
        onShare={shareWith}
        onRevoke={revoke}
        onGetAvailableUsers={getAvailableUsers}
        availableUsers={availableUsers}
        loading={ownershipLoading}
        loadingAvailableUsers={loadingAvailableUsers}
        canManageOwnership={canManageOwnership}
      />
      </div>
    </PagesWrapper>
  );
};

export default SurveyEditor;
